import i18n from 'i18next'
import PropTypes from 'prop-types'
import React from 'react'
import Tooltip from 'react-tooltip'
import {Info} from '../Icons/index.jsx'
import {quoteKinds} from '../Services/api.jsx'
import {simpleModal} from '../Services/domBridge.jsx'
import {
  convertDateWithTimezone,
  currencyToSymbol,
  currencyValueLocalizedFormat,
  dateFnsLocalizedFormat,
  dateFnsLocalizedTimeFormat,
  timeZoneToLabel,
} from '../Services/helpers.jsx'
import ContentFeatures from './ContentFeatures.jsx'
import QuoteResultLabel from './QuoteResultLabel.jsx'

const WithBestSellerBadge = ({
                               children,
                               isBestSeller,
                               isQuoteNotAvailable,
                               isLoading,
                             }) => {
  return !isBestSeller ? (
      children
  ) : (
      <div
          className={'quote-result__header-badge-and-quote-result-features-wrapper'}
      >
        <div className={'quote-result__header-badge-wrapper'}>
        <span
            className={`quote-result__header-badge ${
                isLoading || isQuoteNotAvailable
                    ? 'quote-result__header-badge--hidden'
                    : ''
            }`}
        >
          {i18n.t('Components.QuoteResult.bestSeller')}
        </span>
        </div>
        {children}
      </div>
  )
}
const MoreDetails = ({tooltipId, moreDetailsText, className}) => (
    <>
      <a
          className={
            className
                ? `quote-result__more-details-${className}`
                : 'quote-result__more-details'
          }
          data-tip
          data-for={tooltipId}
      >
        {i18n.t('Components.QuoteResult.moreDetailsLabel')} <Info />
      </a>
      <Tooltip
          id={tooltipId}
          className={'tooltip'}
          effect={'solid'}
          type={'light'}
      >
        <span>{moreDetailsText}</span>
      </Tooltip>
    </>
)

const OrderButton = ({
                       isLoading,
                       quoteKind,
                       isQuoteNotAvailable,
                       onSubmit,
                       quote,
                     }) => (
    <div
        className={`quote-result__order-button ${
            isLoading ? 'quote-result__skeleton' : ''
        }`}
    >
      <button
          data-quote-choice={quoteKind}
          disabled={isQuoteNotAvailable}
          onClick={() => quote && onSubmit?.(quote)}
      >
        {i18n.t(
            isQuoteNotAvailable
                ? 'Components.QuoteResult.orderButtonDisabled'
                : 'Components.QuoteResult.orderButton',
        )}
      </button>
    </div>
)

export default function QuoteResult({
                                      onSubmit,
                                      quote,
                                      timeZone,
                                      quoteKind,
                                      currentCurrency,
                                      rates,
                                      targetLanguagesCount,
                                      openDeliveryDate,
                                      isLoading,
                                      isBestSeller = false,
                                      isAiQuoteEnabled = false,
                                      labelType = null,
                                    }) {
  const getDeliveryDateFormatted = () => {
    if (quoteKind === quoteKinds.lara) {
      return i18n.t('Commons.threeMinutes')
    }

    if (quote) {
      const deliveryDate = convertDateWithTimezone(quote.deliveryDate, timeZone)
      return deliveryDate
          ? `${dateFnsLocalizedFormat(
              deliveryDate,
              'MMM d',
          )}, ${dateFnsLocalizedTimeFormat(deliveryDate)} ${timeZoneToLabel(
              timeZone,
          )}`
          : ''
    } else {
      return '▌▄ ▌▌ ▌▄ ▐▌:▐▌'
    }
  }

  const getDeliveryBefore = () => {
    let before = false

    if (quote && quote.deliveryDate.date) {
      if (
          quote.deliveryDate.date >
          convertDateWithTimezone(quote.deliveryDate, timeZone)
      ) {
        before = true
      }
    }
    return before
  }

  const getPriceFormatted = () => {
    const currencyRate = rates?.[currentCurrency] || 1
    const currencySymbol = currencyToSymbol(currentCurrency)

    if (quote) {
      const price = quote.price * currencyRate
      return `${currencySymbol}${currencyValueLocalizedFormat(price)}`
    } else {
      return '▄ ▌▐▐▐'
    }
  }

  const getPricePerWordFormatted = () => {
    const currencyRate = rates?.[currentCurrency] || 1
    const currencySymbol = currencyToSymbol(currentCurrency)

    if (quote) {
      const price = quote.price * currencyRate
      const priceWord = Number(
          (price / quote.words / targetLanguagesCount).toFixed(2),
      )
      const priceFormatted = `${currencySymbol}${currencyValueLocalizedFormat(
          priceWord,
          {maximumFractionDigits: 3},
      )}`
      return priceFormatted
    } else {
      return '▄ ▌▐▐▐'
    }
  }

  const getMoreDetailsText = () => {
    switch (quoteKind) {
      case quoteKinds.translationRevision:
        return i18n.t('Components.QuoteResult.premiumDetails')
      case quoteKinds.translation:
        return i18n.t('Components.QuoteResult.professionalDetails')
      case quoteKinds.postediting:
        return i18n.t('Components.QuoteResult.economyDetails')
      case quoteKinds.transcreation:
        return i18n.t('Components.QuoteResult.transcreationDetails')
      case quoteKinds.lara:
        return i18n.t('Components.QuoteResult.instantDetails')
      default:
        return ''
    }
  }

  const getDeliveryGuaranteedLabel = () => {
    if (quoteKind === quoteKinds.lara) {
      return i18n.t('Components.QuoteResult.deliveryGuaranteedWithin')
    }
    return i18n.t('Components.QuoteResult.deliveryGuaranteedBy')
  }

  const deliveryModal = () => {
    simpleModal(
        i18n.t('Components.QuoteResult.deliveryBeforeTitle'),
        i18n.t('Components.QuoteResult.deliveryBeforeDescription'),
        'date',
    )
  }

  const renderFeatures = () => {
    switch (quoteKind) {
      case quoteKinds.translationRevision:
        return (
            <WithBestSellerBadge
                isQuoteNotAvailable={!isLoading && !quote}
                isLoading={isLoading}
                isBestSeller={isBestSeller}
            >
              <ContentFeatures
                  title={i18n.t('Components.QuoteResult.premium')}
                  quoteKind={quoteKinds.translationRevision}
              />
            </WithBestSellerBadge>
        )

      case quoteKinds.translation:
        return (
            <WithBestSellerBadge
                isQuoteNotAvailable={!isLoading && !quote}
                isLoading={isLoading}
                isBestSeller={isBestSeller}
            >
              <ContentFeatures
                  title={i18n.t('Components.QuoteResult.professional')}
                  quoteKind={quoteKinds.translation}
              />
            </WithBestSellerBadge>
        )

      case quoteKinds.postediting:
        return (
            <ContentFeatures
                title={i18n.t('Components.QuoteResult.economy')}
                quoteKind={quoteKinds.postediting}
            />
        )
      case quoteKinds.transcreation:
        return (
            <ContentFeatures
                title={i18n.t('Components.QuoteResult.transcreation')}
                quoteKind={quoteKinds.transcreation}
            />
        )
      case quoteKinds.lara:
        return (
            <ContentFeatures
                title={i18n.t('Commons.instantLabel')}
                quoteKind={quoteKinds.lara}
            />
        )
      default:
        return undefined
    }
  }

  const isQuoteNotAvailable = !isLoading && !quote
  const tooltipId = `Tooltip-${quoteKind}`
  const deliveryBefore = getDeliveryBefore()
  const deliveryDateFormatted = getDeliveryDateFormatted()
  const priceFormatted = getPriceFormatted()
  const pricePerWordFormatted = getPricePerWordFormatted()
  const moreDetailsText = getMoreDetailsText()

  const cardContent = (
      <div
          role="region"
          aria-label={`${quoteKind} card`}
          className={`quote-result ${
              isAiQuoteEnabled ? 'quote-result--no-border' : ''
          }`}
      >
        {renderFeatures()}
        {isQuoteNotAvailable ? (
            <div className={'quote-result__delivery-date'}>
              <p className={'quote-result__delivery-date-label'}>
                {i18n.t('Components.QuoteResult.solutionNotAvailable')}
              </p>
              <p className={'quote-result__delivery-date-label'}>
                <i onClick={openDeliveryDate}>
                  {i18n.t('Components.QuoteResult.solutionNotAvailableTry')}
                </i>
              </p>
              <p
                  className={'quote-result__delivery-date-not-available'}
                  dangerouslySetInnerHTML={{
                    __html: i18n.t(
                        'Components.QuoteResult.solutionNotAvailableContact',
                    ),
                  }}
              />
            </div>
        ) : (
            <div className={'quote-result__delivery-date'}>
              <p className={'quote-result__delivery-date-label'}>
                {getDeliveryGuaranteedLabel()}
              </p>
              <p
                  className={`quote-result__delivery-date-date ${
                      isLoading ? 'quote-result__skeleton' : ''
                  } ${
                      quoteKind === quoteKinds.lara
                          ? 'quote-result__delivery-date-date--no-capitalize'
                          : ''
                  }`.trim()}
              >
            <span className={deliveryBefore ? 'date-before' : null}>
              {deliveryDateFormatted}{' '}
              {deliveryBefore && (
                  <span onClick={deliveryModal}>
                  ({i18n.t('Components.QuoteResult.deliveryBeforeLink')})
                </span>
              )}
            </span>
                {!isLoading && (
                    <a
                        onClick={openDeliveryDate}
                        style={{
                          visibility:
                              quoteKind !== quoteKinds.lara ? 'visible' : 'hidden',
                        }}
                    >
                      {i18n.t('Components.QuoteResult.needItFaster')}
                    </a>
                )}
              </p>
            </div>
        )}

        {isQuoteNotAvailable ? (
            <div className={'quote-result__price-and-order-wrapper'}>
              <OrderButton
                  isLoading={isLoading}
                  isQuoteNotAvailable={isQuoteNotAvailable}
                  quoteKind={quoteKind}
                  onSubmit={onSubmit}
                  quote={quote}
              />
              <MoreDetails
                  tooltipId={tooltipId}
                  moreDetailsText={moreDetailsText}
              />
            </div>
        ) : (
            <div className={'quote-result__price-and-order-wrapper-available'}>
              <div className="quote-result__price">
                <div className={'quote-result__price-wrapper'}>
                  <p
                      className={`quote-result__price-price ${
                          isLoading ? 'quote-result__skeleton' : ''
                      }`}
                  >
                    <span>{priceFormatted}</span>
                    {currentCurrency &&
                        currencyToSymbol(currentCurrency) === '$' && (
                            <span className="quote-result__price-currency">
                      {' '}
                              ({currentCurrency})
                    </span>
                        )}
                  </p>

                  <p
                      className={`quote-result__price-per-word ${
                          isLoading ? 'quote-result__skeleton' : ''
                      }`}
                  >
                <span>
                  {i18n.t('Components.QuoteResult.aboutWord', {
                    price: pricePerWordFormatted,
                  })}
                </span>
                  </p>
                </div>

                <OrderButton
                    isLoading={isLoading}
                    isQuoteNotAvailable={isQuoteNotAvailable}
                    quoteKind={quoteKind}
                    onSubmit={onSubmit}
                    quote={quote}
                />
              </div>

              <MoreDetails
                  tooltipId={tooltipId}
                  moreDetailsText={moreDetailsText}
                  className={'available'}
              />
            </div>
        )}
      </div>
  )

  if (isAiQuoteEnabled && labelType) {
    return (
        <div className="quote-result-wrapper">
          <div className="quote-result__mobile-label">
            <QuoteResultLabel label={labelType} />
          </div>
          {cardContent}
        </div>
    )
  }

  return cardContent
}

QuoteResult.propTypes = {
  quoteKind: PropTypes.oneOf([
    'postediting',
    'translation',
    'translation+revision',
    'transcreation',
    'lara',
    'lara_instant',
  ]),
  quote: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    code: PropTypes.string,
    quoteKind: PropTypes.oneOf([
      'postediting',
      'translation',
      'translation+revision',
      'transcreation',
      'lara',
      'lara_instant',
    ]),
    deliveryDate: PropTypes.instanceOf(Date),
    price: PropTypes.number,
    words: PropTypes.number,
  }),
  isLoading: PropTypes.bool,
  onSubmit: PropTypes.func,
  currentCurrency: PropTypes.string,
  rates: PropTypes.any,
  timeZone: PropTypes.shape({
    id: PropTypes.string,
    name: PropTypes.string,
    label: PropTypes.string,
  }),
  openDeliveryDate: PropTypes.func,
  targetLanguagesCount: PropTypes.number,
  isBestSeller: PropTypes.bool,
  isAiQuoteEnabled: PropTypes.bool,
  labelType: PropTypes.string,
}
